import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ArrowLeft,
  FileText,
  Edit3,
  CheckCircle,
  AlertCircle,
  FileDown,
  ExternalLink,
  XCircle
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useHourmeterDetail } from '../../hooks/useHourmeters';
import { hourmeterService } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import SignatureCanvas, { type SignatureCanvasRef } from '../../components/SignatureCanvas';
import {
  dataURLtoBlob,
  blobToFile,
  isValidEmail
} from '../../utils';
import type { SignatureFormData, RejectFormData } from '../../types';

// Validation schema
const signatureSchema: yup.ObjectSchema<SignatureFormData> = yup.object({
  email: yup
    .string()
    .nullable()
    .optional()
    .test('valid-email', 'Formato de email inválido', (value) =>
      !value || isValidEmail(value)
    ),
  observations: yup.string().nullable().optional().max(1000, 'Máximo 1000 caracteres'),
});

const rejectSchema: yup.ObjectSchema<RejectFormData> = yup.object({
  comments: yup
    .string()
    .required('Los comentarios de rechazo son obligatorios')
    .min(10, 'Los comentarios deben tener al menos 10 caracteres')
    .max(1000, 'Máximo 1000 caracteres'),
});

const Receipt: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { operator } = useAuth();

  const [isSigningMode, setIsSigningMode] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);

  // Estados para verificación del residente
  const [residentStep, setResidentStep] = useState<'choose-action' | 'sign' | 'reject'>('choose-action');
  const [residentVerified, setResidentVerified] = useState(false);
  const [residentError, setResidentError] = useState<string | null>(null);

  // Estados para rechazo del residente
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  // El PDF ya no se puede pedir por URL suelta: la ruta exige sesión y un
  // `<embed src>` no manda cabeceras. Se baja por axios y se pinta el blob.
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);

  const signatureRef = useRef<SignatureCanvasRef>(null);

  const hourmeterId = id ? parseInt(id, 10) : null;
  const { hourmeter, loading, error } = useHourmeterDetail(hourmeterId);

  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'sign' && hourmeter && !hourmeter.isSigned) {
      checkAndHandleSession();
    }
  }, [searchParams, hourmeter]);

  useEffect(() => {
    if (!hourmeter || !operator) return;

    let cancelado = false;
    let urlCreada: string | null = null;

    hourmeterService
      .getReceiptPdfObjectUrl(hourmeter.id, operator.id, (operator.role || 'operator') as 'operator' | 'supervisor' | 'resident')
      .then((url) => {
        if (cancelado) {
          // Llegó tarde: nadie lo va a pintar y sin revocar queda ocupando
          // memoria hasta que se recargue la página.
          URL.revokeObjectURL(url);
          return;
        }
        urlCreada = url;
        setPdfObjectUrl(url);
      })
      .catch(() => {
        if (!cancelado) setPdfObjectUrl(null);
      });

    return () => {
      cancelado = true;
      if (urlCreada) URL.revokeObjectURL(urlCreada);
    };
  }, [hourmeter, operator]);

  const form = useForm<SignatureFormData>({
    resolver: yupResolver(signatureSchema),
    defaultValues: {
      email: operator?.email || '',
      observations: '',
    },
  });

  const rejectForm = useForm<RejectFormData>({
    resolver: yupResolver(rejectSchema),
    defaultValues: {
      comments: '',
    },
  });

  // Actualizar observaciones cuando se cargan los datos del horometro
  useEffect(() => {
    if (hourmeter?.defaultObservations) {
      // Si hay observaciones del supervisor, usar esas; si no, usar las por defecto
      const observations = hourmeter.residentObservations || hourmeter.defaultObservations || '';
      form.setValue('observations', observations);
    }
  }, [hourmeter, form]);

  const handleBack = () => {
    navigate('/dashboard', {
      state: { shouldRefresh: true }
    });
  };

  // Función para verificar sesión y configurar estado apropiado
  const checkAndHandleSession = () => {
    setIsSigningMode(true);

    // Tanto residentes como operadores van directo a elegir acción (sin OTP)
    setResidentVerified(true);
    setResidentStep('choose-action');

    // Limpiar errores y estados
    setSubmitError(null);
    setSubmitSuccess(false);
    setRejectSuccess(false);
    setResidentError(null);
    setRejectError(null);
  };

  const handleStartSigning = () => {
    checkAndHandleSession();
  };

  const handleViewPDF = () => {
    if (!pdfObjectUrl) return;
    window.open(pdfObjectUrl, '_blank');
  };

  const handleDownloadPDF = () => {
    if (!pdfObjectUrl || !hourmeter) return;
    // El `download=true` del endpoint solo servía para que el navegador armara
    // el nombre del archivo; con el blob se pone acá y se ahorra un request.
    const a = document.createElement('a');
    a.href = pdfObjectUrl;
    a.download = `recibo-horometro-${hourmeter.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSignatureChange = (signature: string | null) => {
    setSignatureData(signature);
  };

  const handleSubmitSignature = async (data: SignatureFormData) => {
    if (!operator || !hourmeter || !signatureData) {
      setSubmitError('Datos incompletos para enviar la firma');
      return;
    }

    if (signatureRef.current?.isEmpty()) {
      setSubmitError('Por favor, agrega tu firma antes de continuar');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Convert signature to file
      const blob = dataURLtoBlob(signatureData);
      const file = blobToFile(blob, `signature_${hourmeter.id}.png`);

      const response = await hourmeterService.signHourmeter({
        hourmeterId: hourmeter.id,
        operatorId: operator.id,
        email: data.email || undefined,
        observations: data.observations || undefined,
        residentVerified: residentVerified,
        signature: file,
      }, operator.role || 'operator');

      if (response.status === 'success') {
        setSubmitSuccess(true);
        setIsSigningMode(false);

        // Show success message and redirect after delay
        setTimeout(() => {
          navigate('/dashboard', {
            state: { shouldRefresh: true }
          });
        }, 3000);
      } else {
        setSubmitError(response.message);
      }
    } catch (err: any) {
      console.error('Error submitting signature:', err);
      setSubmitError(
        err.response?.data?.message ||
        'Error al enviar la firma. Intenta nuevamente.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Funciones para rechazo
  const handleStartReject = () => {
    checkAndHandleSession();
  };

  const handleSubmitReject = async (data: RejectFormData) => {
    if (!operator || !hourmeter || !residentVerified) {
      setRejectError('Debe completar la verificación del residente');
      return;
    }

    setRejectSubmitting(true);
    setRejectError(null);

    try {
      const response = await hourmeterService.rejectHourmeter({
        hourmeterId: hourmeter.id,
        operatorId: operator.id,
        comments: data.comments,
        residentVerified: residentVerified,
        role: operator.role || 'operator'
      });

      if (response.status === 'success') {
        setRejectSuccess(true);
        setIsSigningMode(false);

        // Show success message and redirect after delay
        setTimeout(() => {
          navigate('/dashboard', {
            state: { shouldRefresh: true }
          });
        }, 3000);
      } else {
        setRejectError(response.message);
      }
    } catch (err: any) {
      console.error('Error rejecting hourmeter:', err);
      setRejectError(
        err.response?.data?.message ||
        'Error al rechazar el horometro. Intenta nuevamente.'
      );
    } finally {
      setRejectSubmitting(false);
    }
  };

  // Detectar si es Android
  // El visor de Google (`docs.google.com/gview`) ya no se puede usar: pedía el
  // PDF desde los servidores de Google, y eso solo funciona con una URL pública
  // —que es justamente lo que se cerró—. El blob se pinta igual en el `<embed>`.
  const getViewerUrl = (url: string) => `${url}#zoom=page-fit`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <Loading size="lg" text="Cargando recibo..." />
      </div>
    );
  }

  if (error || !hourmeter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="max-w-md w-full">
          <Alert variant="error" title="Error">
            {error || 'No se pudo cargar el recibo'}
          </Alert>
          <div className="mt-4 text-center">
            <Button variant="primary" onClick={() => navigate('/dashboard', { state: { shouldRefresh: true } })}>
              Volver al Registro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-50 rounded-lg border border-gray-300 p-8 shadow-md">
            <CheckCircle className="mx-auto h-12 w-12 text-retri-green mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Recibo Firmado!
            </h2>
            <p className="text-gray-700 mb-6">
              La firma del residente ha sido registrada exitosamente.
            </p>

            {/* Botones PDF */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button
                variant="outline"
                onClick={handleViewPDF}
                className="flex items-center justify-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                className="flex items-center justify-center"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            </div>

            <p className="text-sm text-gray-500">
              Redirigiendo al registro...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (rejectSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-50 rounded-lg border border-gray-300 p-8 shadow-md">
            <XCircle className="mx-auto h-12 w-12 text-retri-red mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Horometro Rechazado!
            </h2>
            <p className="text-gray-700 mb-6">
              El residente ha rechazado el horometro con comentarios. El operador podrá ver los motivos y corregir el horometro.
            </p>

            <p className="text-sm text-gray-500">
              Redirigiendo al registro...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-retri-blue shadow-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              size="sm"
              className="flex-shrink-0 text-white hover:bg-retri-bluedark"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{isSigningMode ? 'Volver al Recibo' : 'Volver al Registro'}</span>
              <span className="inline sm:hidden">Volver</span>
            </Button>

            <div className="text-center min-w-0 mx-2">
              <h1 className="text-sm sm:text-lg font-semibold text-white truncate">
                {isSigningMode ? (operator?.role === 'resident' ? 'Firmar' : 'Residente Firma') : 'Recibo de Trabajo'}
              </h1>
            </div>

            <div className="w-16 sm:w-24 flex-shrink-0"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {!isSigningMode ? (
          /* Receipt View - PDF Embedded */
          <div className="space-y-4 sm:space-y-6">
            {/* Receipt Header with Status */}
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-retri-blue flex-shrink-0" />
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recibo de Trabajo</h2>
                  </div>
                  <div className="flex items-center">
                    {hourmeter.isSigned ? (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-retri-green text-white">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Firmado
                      </span>
                    ) : hourmeter.isRejected ? (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-retri-red text-white">
                        <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">Rechazado por Residente</span>
                        <span className="inline sm:hidden">Rechazado</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-retri-orange text-white">
                        <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">Pendiente de Firma</span>
                        <span className="inline sm:hidden">Pendiente</span>
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rejection Comments - Show if rejected (Moved above PDF) */}
            {hourmeter.isRejected && hourmeter.residentRejectComments && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-red-600">
                    <XCircle className="h-5 w-5 mr-2" />
                    Comentarios del Rechazo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">
                      Rechazado el: {hourmeter.residentRejectDate && new Date(hourmeter.residentRejectDate).toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="text-gray-900">
                      {hourmeter.residentRejectComments}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PDF Embedded - Now Much Larger */}
            <Card>
              <CardContent className="p-0">
                <div className="w-full h-[75vh] sm:h-[85vh] lg:h-[90vh] bg-gray-100 rounded-lg overflow-hidden">
                  {pdfObjectUrl ? (
                    <iframe
                      src={getViewerUrl(pdfObjectUrl)}
                      className="w-full h-dvh border-0"
                      title="Recibo de Trabajo PDF"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loading size="lg" text="Cargando recibo..." />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3 sm:space-y-4">
              {!hourmeter.isSigned && (
                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleStartSigning}
                    className="w-full sm:w-auto h-12 sm:h-auto"
                  >
                    <Edit3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    {hourmeter.isRejected ? 'Re-firmar' : (operator?.role === 'resident' ? 'Firmar' : 'Residente Firma')}
                  </Button>
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={handleStartReject}
                    className="w-full sm:w-auto h-12 sm:h-auto"
                  >
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Rechazar
                  </Button>
                </div>
              )}

              {/* PDF Buttons - Available for both signed and unsigned */}
              <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleViewPDF}
                  className="w-full sm:w-auto h-11 sm:h-auto"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver PDF
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleDownloadPDF}
                  className="w-full sm:w-auto h-11 sm:h-auto"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Signature View - Reorganized for better PDF visibility */
          <div className="space-y-4 sm:space-y-6">
            {/* PDF Preview - Now Always Visible and Large */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-base sm:text-lg">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-retri-blue" />
                  Vista Previa del Recibo
                </CardTitle>
              </CardHeader>

              <CardContent className="p-0">
                <div className="w-full h-[70vh] sm:h-[75vh] lg:h-[80vh] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {pdfObjectUrl ? (
                    <embed
                      src={getViewerUrl(pdfObjectUrl)}
                      type="application/pdf"
                      className="w-full h-full object-contain border-0"
                      title="Vista Previa Recibo PDF"
                    />
                  ) : (
                    <Loading size="lg" text="Cargando recibo..." />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Signature Form - Now Below PDF */}
            <Card>
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Firma del Residente</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {/* Errores del residente */}
                {residentError && (
                  <Alert variant="error" className="mb-4">
                    {residentError}
                  </Alert>
                )}

                {/* Paso 3: Elección de Acción (Firmar o Rechazar) */}
                {residentStep === 'choose-action' && residentVerified && (
                  <div className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Botón Firmar */}
                      <div className="border-2 border-retri-green rounded-lg p-6 hover:bg-green-50 transition-colors cursor-pointer"
                        onClick={() => setResidentStep('sign')}>
                        <div className="text-center">
                          <CheckCircle className="mx-auto h-12 w-12 text-retri-green mb-4" />
                          <h4 className="text-lg font-semibold text-green-800 mb-2">Firmar y Aprobar</h4>
                          <p className="text-sm text-gray-700 mb-4">
                            El horometro es correcto y puede ser aprobado
                          </p>
                          <Button
                            variant="success"
                            size="lg"
                            onClick={() => setResidentStep('sign')}
                            className="w-full"
                          >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Firmar
                          </Button>
                        </div>
                      </div>

                      {/* Botón Rechazar */}
                      <div className="border-2 border-retri-red rounded-lg p-6 hover:bg-red-50 transition-colors cursor-pointer"
                        onClick={() => setResidentStep('reject')}>
                        <div className="text-center">
                          <XCircle className="mx-auto h-12 w-12 text-retri-red mb-4" />
                          <h4 className="text-lg font-semibold text-red-800 mb-2">Rechazar con Comentarios</h4>
                          <p className="text-sm text-gray-700 mb-4">
                            El horometro requiere correcciones
                          </p>
                          <Button
                            variant="danger"
                            size="lg"
                            onClick={() => setResidentStep('reject')}
                            className="w-full"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                )}

                {/* Paso 4: Formulario de firma completo */}
                {residentStep === 'sign' && residentVerified && (
                  <form onSubmit={form.handleSubmit(handleSubmitSignature)} className="space-y-6">
                    {submitError && (
                      <Alert variant="error">
                        {submitError}
                      </Alert>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Firma del Residente *
                      </label>
                      <SignatureCanvas
                        ref={signatureRef}
                        onSignatureChange={handleSignatureChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Observaciones (opcional)
                      </label>
                      <textarea
                        {...form.register('observations')}
                        className="w-full px-3 py-2 border-2 border-gray-400 rounded-md shadow-sm text-base text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-retri-blue focus:border-retri-blue"
                        rows={3}
                        placeholder="Las observaciones por defecto aparecerán automáticamente. Puedes agregar observaciones adicionales aquí..."
                      />
                      {form.formState.errors.observations && (
                        <p className="mt-1 text-sm text-red-600">
                          {form.formState.errors.observations.message}
                        </p>
                      )}
                    </div>
                    <Input
                      {...form.register('email')}
                      type="email"
                      label="Enviar copia de recibo a este correo:"
                      placeholder="ejemplo@correo.com"
                      error={form.formState.errors.email?.message}
                      helpText="Si proporcionas tu email, recibirás una copia del recibo firmado"
                    />
                    {/* Submit Button */}
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        variant="success"
                        disabled={submitting || !signatureData}
                        className="flex items-center"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Confirmar Firma
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Paso 4: Formulario de Rechazo */}
                {residentStep === 'reject' && residentVerified && (
                  <form onSubmit={rejectForm.handleSubmit(handleSubmitReject)} className="space-y-6">
                    {rejectError && (
                      <Alert variant="error">
                        {rejectError}
                      </Alert>
                    )}

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-red-800">
                        <strong>⚠️ Rechazar Horometro:</strong> Indique los motivos del rechazo. El operador podrá ver estos comentarios y corregir el horometro.
                      </p>
                    </div>

                    {/* Comments Input */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Comentarios del Rechazo *
                      </label>
                      <textarea
                        {...rejectForm.register('comments')}
                        className="w-full px-3 py-2 border-2 border-gray-400 rounded-md shadow-sm text-base text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        rows={5}
                        placeholder="Describa detalladamente los motivos del rechazo (mínimo 10 caracteres)..."
                      />
                      {rejectForm.formState.errors.comments && (
                        <p className="mt-1 text-sm text-red-600">
                          {rejectForm.formState.errors.comments.message}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        variant="danger"
                        disabled={rejectSubmitting}
                        className="flex items-center bg-red-600 hover:bg-red-700 text-white"
                      >
                        {rejectSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Rechazando...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Confirmar Rechazo
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default Receipt;
